import React, { useState, useRef, useEffect, useContext } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { useQuery, useMutation } from 'react-apollo-hooks';
import { loader } from 'graphql.macro';
import cn from 'classnames';

import { ProfileContext } from '@loyalup/core';
import { WithTitle, Button, Loader, FileUploader } from '@loyalup/ui';

import ClientImportColumn from 'components/ClientImportColumn';

import updateProfileInCache from 'utils/updateProfileInCache';

import { ReactComponent as BackIcon } from 'icons/go-back.svg';
import { ReactComponent as ReloadIcon } from 'icons/reload.svg';
import { ReactComponent as CircleXIcon } from 'icons/circle-x.svg';
import { ReactComponent as ClientsAddedIcon } from 'icons/clients-added.svg';
import { ReactComponent as ClientsUpdatedIcon } from 'icons/clients-updated.svg';
import { ReactComponent as CircleArrowIcon } from 'icons/circle-arrow.svg';

import s from './ClientImport.module.css';

const CLIENT_IMPORT_QUERY = loader('../../gql/queries/ClientImport.gql');
const CREATE_IMPORT_MUTATION = loader('../../gql/mutations/CreateClientImport.gql');
const PROCESS_IMPORT_MUTATION = loader('../../gql/mutations/ProcessClientImport.gql');

const ClientImport = ({ match, history }) => {
  const { clientsCount } = useContext(ProfileContext);

  const uploadRef = useRef();
  const firstErrorColumnRef = useRef();
  const columnsRef = useRef();
  const [t, i18n] = useTranslation();
  const { sid } = match.params;

  const [clientImport, setClientImport] = useState();
  const [validCounter, setValidCounter] = useState(0);
  const [validationError, setValidationError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data } = useQuery(CLIENT_IMPORT_QUERY, { suspend: false, variables: { sid } });

  if (clientImport && clientImport.sid !== sid) {
    setClientImport(undefined);
    setUploading(false);
  }

  if (data && data.clientImport && !clientImport) {
    setClientImport(data.clientImport);
  }

  const [processImport] = useMutation(PROCESS_IMPORT_MUTATION, {
    update: (cache, { data: { processClientImport } }) => {
      updateProfileInCache(cache, { clientsCount: clientsCount + processClientImport.clientsAdded });
    },
  });

  const [createImport] = useMutation(CREATE_IMPORT_MUTATION, {
    update: (cache, { data: { createClientImport } }) => {
      const { sid } = createClientImport;
      history.push(`/clients/imports/${sid}`);
    },
  });

  const runImport = async () => {
    let valid = true;
    let noPhone = true;
    const columns = clientImport.columns.slice();
    clientImport.columns.forEach((column, index) => {
      if (!column.skipped && !column.field) {
        valid = false;
        columns.splice(index, 1, { ...column, error: true });
      }

      if (!column.skipped && column.field === 'phone') {
        noPhone = false;
      }
    });

    if (!valid || noPhone) {
      setClientImport({ ...clientImport, columns });
      setValidationError(noPhone && t('Необходимо указать столбец "Телефон"'));
      setValidCounter(validCounter + 1);
      return;
    }

    const bindings = clientImport.columns.map(({ field, skipped }) => (skipped ? '' : field));

    setLoading(true);
    const { data } = await processImport({ variables: { sid, bindings } });
    setClientImport(data.processClientImport);
    setLoading(false);
  };

  useEffect(() => {
    const columnsEl = columnsRef.current;
    const errorColumnEl = firstErrorColumnRef.current;
    if (columnsEl && errorColumnEl) {
      columnsEl.scrollTo(errorColumnEl.offsetLeft - errorColumnEl.offsetWidth, 0);
    }
  }, [validCounter]);

  const correctImport = clientImport && !clientImport.error && clientImport.status === 'parsed';
  const completedImport = clientImport && clientImport.status === 'imported';
  const errorImport = clientImport && clientImport.error;

  const actualColumns = (clientImport && clientImport.columns) || [];
  const firstErrorColumn = actualColumns.filter(c => c.error && !c.skipped)[0];

  return (
    <WithTitle title={t('CSV Импорт')}>
      {correctImport && (
        <div className={s.root}>
          <div className={s.header}>
            <Link to="/clients/list" className={s.back}>
              <BackIcon />
            </Link>
            <h1>{clientImport.filename}</h1>
            <Button fixedWidth={200} center onClick={runImport} loading={loading} disabled={uploading}>
              {t('Добавить клиентов')}
            </Button>
          </div>
          <div className={s.info}>
            <button
              className={s.reupload}
              disabled={loading || uploading}
              onClick={() => uploadRef.current.selectFile()}
            >
              {t('Выбрать другой файл')}
              {uploading && <Loader className={s.loader} />}
            </button>
            {!validationError && (
              <div className={s.hint}>
                {t('Клиенты с одинаковыми телефонными номерами, емейлами или номерами карт будут объединены.')}
              </div>
            )}
            {validationError && <div className={s.error}>{validationError}</div>}
            <div className={s.counter}>{t('Найдено {{count, number}} строк', { count: clientImport.totalCount })}</div>
          </div>
          <div className={s.columns}>
            <div className={s.container} ref={columnsRef}>
              {actualColumns.map((column, index) => (
                <div key={column.index} className={s.columnContainer}>
                  <ClientImportColumn
                    ref={ref => {
                      if (column === firstErrorColumn) {
                        firstErrorColumnRef.current = ref;
                      }
                    }}
                    column={column}
                    className={s.column}
                    disabled={loading || uploading}
                    onChange={column => {
                      const columns = clientImport.columns.slice();
                      columns.splice(index, 1, column);
                      setClientImport({ ...clientImport, columns });
                      if (column.field === 'phone' && !column.skipped) setValidationError(false);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {errorImport && (
        <div className={cn(s.rootCentered, s.error)}>
          <div className={s.header}>
            <Link to="/clients/list" className={s.back}>
              <BackIcon />
            </Link>
            <h1>{clientImport.filename}</h1>
          </div>
          <div className={s.container}>
            <div className={s.panelCentered}>
              <div className={cn(s.square, s.error, s.panel)}>
                <CircleXIcon className={s.icon} />
                {t('Не удалось прочитать файл')}
              </div>
              <div className={s.panelText}>
                <div className={s.title}> {t('Ошибка импорта')}</div>
                <strong>{t('Возможные причины:')}</strong>
                <ul>
                  <li>{t('Файл пустой.')}</li>
                  <li>{t('Неверный формат файла, убедитесь что файл формата CSV.')}</li>
                  <li>
                    {t(
                      'Файл имеет неизвестную кодировку, убедитесь что файл содержит только русские или английские буквы, цифры.',
                    )}
                  </li>
                </ul>
              </div>
              <Button
                theme="square"
                className={s.square}
                loading={uploading}
                onClick={() => uploadRef.current.selectFile()}
              >
                <ReloadIcon className={s.icon} />
                <Trans t={t} i18n={i18n} defaults="Загрузить <0/> другой файл" components={[<br key="br" />]} />
              </Button>
              <div className={s.panelText}>
                {/* fix link to go to chat */}
                <Trans
                  t={t}
                  i18n={i18n}
                  defaults="Мы автоматически анализируем файлы с ошибками и стараемся улучшить наши алгоритмы работы с импортом. Если вам необходимо срочно импортировать именно этот файл и вы не знаете как его исправить - <0>отправьте нам запрос</0> с вашим комментарием, мы разберемся с причиной и свяжемся с вами в ближайшее время."
                  components={[<Link to="/clients/list">link</Link>]}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {completedImport && (
        <div className={cn(s.rootCentered, s.done)}>
          <div className={s.header}>
            <Link to="/clients/list" className={s.back}>
              <BackIcon />
            </Link>
            <h1>{clientImport.filename}</h1>
          </div>
          <div className={s.container}>
            <div className={s.panelCentered}>
              <div className={cn(s.square, s.stats, s.panel)}>
                <ClientsAddedIcon className={s.icon} />
                {t('Добавлено клиентов')}
                <div className={s.number}>{t('{{count, number}}', { count: clientImport.clientsAdded })}</div>
              </div>
              <div className={cn(s.square, s.stats, s.panel)}>
                <ClientsUpdatedIcon className={s.icon} />
                {t('Обновлено клиентов')}
                <div className={s.number}>{t('{{count, number}}', { count: clientImport.clientsReplaced })}</div>
              </div>
              <Button element={Link} theme="square" className={cn(s.square, s.stats)} to="/clients/list">
                <CircleArrowIcon className={s.icon} />
                <Trans t={t} i18n={i18n} defaults="К списку<0/>клиентов" components={[<br key="br" />]} />
              </Button>
            </div>
          </div>
        </div>
      )}
      <FileUploader
        hidden
        accept="text/csv"
        type="csv"
        server={process.env.REACT_APP_SERVER_URL}
        onStart={() => setUploading(true)}
        onFinish={({ filename, path }) => createImport({ variables: { filename, path } })}
        ref={uploadRef}
      />
    </WithTitle>
  );
};

export default withRouter(ClientImport);
